import _ from "lodash";
import client from "lib/apolloClient/client";
import { updateUserCoursesMutation } from "./CourseMutations";
import { getPlatformUserDetailsQuery } from "modules/CommonQuery";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getYearDurationFormat, isNumber } from "Utils";
import * as Sentry from "@sentry/browser";
import {
  getUserIdAndEntityTypeByUserMemoize,
  setGradeSection,
} from "Teacher/modules/TeacherModule";
import ACLStore from "lib/aclStore";
import update from "immutability-helper";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import {
  CURRICULUM_TYPE_PYP,
  DP_SUBJECT_SPECIFIC,
  MYP_SUBJECT_SPECIFIC,
  UBD_SUBJECT_SPECIFIC,
  NETWORK_ONLY,
} from "Constants/stringConstants";
import {
  ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS,
  LIST_AND_ACCESS_ALL_CLASSES_PERMISSION,
} from "Constants/permissionConstants";
import {
  getCourseDetailsQuery,
  getUserCoursesBasicDetailsQuery,
} from "./CourseQuery";
import {
  getUserCourseAccessQueryFromCache,
  getCourseDetailsFromCache,
} from "./CourseGraphqlHelpers";
import ACLStorage from "lib/aclService";
import { goToPlatformHomeChildRoute } from "modules/NavigationModule";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

// constants
export const NAME = "course";
export const UPDATE_COURSE_NAVIGATION_MENU =
  "UPDATE_COURSE_NAVIGATION_MENU" + " " + NAME;
export const RESET_COURSE_NAVIGATION_MENU =
  "RESET_COURSE_NAVIGATION_MENU" + " " + NAME;

//Action creators
export const updateCourseNavigationMenu = data => {
  return {
    type: UPDATE_COURSE_NAVIGATION_MENU,
    data,
  };
};

export const resetCourseNavigationMenu = () => {
  return {
    type: RESET_COURSE_NAVIGATION_MENU,
  };
};

export const getUserCoursesFilters = _.memoize(
  ({
    organizationCurriculumPrograms,
    currentCurriculumProgramId,
    archivalState = "ALL",
  }) => {
    const curriculumProgram = _.find(organizationCurriculumPrograms, {
      id: currentCurriculumProgramId,
    });
    const curriculumProgramIds = _.filter(
      curriculumProgram ? [curriculumProgram.id] : [],
      Boolean
    );
    if (_.size(curriculumProgramIds) === 0) {
      Sentry.addBreadcrumb({
        category: "console",
        level: "info",
        message: JSON.stringify({ curriculumProgram }, null, 4),
      });
    }
    return {
      curriculumProgramIds,
      archivalState,
    };
  },
  params => JSON.stringify(params)
);

export const getUserCoursesFiltersV2 = _.memoize(
  ({
    organizationCurriculumPrograms,
    currentCurriculumProgramId,
    curriculumProgramIds,
    archivalState = "ALL",
  }) => {
    const curriculumProgram = _.find(organizationCurriculumPrograms, {
      id: currentCurriculumProgramId,
    });

    if (!curriculumProgramIds) {
      curriculumProgramIds = _.filter(
        curriculumProgram ? [curriculumProgram.id] : [],
        Boolean
      );
    }

    if (_.size(curriculumProgramIds) === 0) {
      Sentry.addBreadcrumb({
        category: "console",
        level: "info",
        message: JSON.stringify({ curriculumProgram }, null, 4),
      });
    }
    return {
      curriculumProgramIds,
      archivalState,
    };
  },
  params => JSON.stringify(params)
);

/**
 *
 * @description returns default query vars
 */
export const getUserCoursesVars = ({
  userInfo,
  platform,
  isCurriculumProgramFree,
}) => {
  const {
    currentCurriculumProgramType,
    currentCurriculumProgram,
    organizationCurriculumPrograms,
    academicYearSelected = {},
  } = platform;
  const {
    isCurrentAcademicYear = true,
    id: academicYear = "",
  } = academicYearSelected;

  const { userId, userEntityType } = getUserIdAndEntityTypeByUserMemoize({
    userInfo,
  });

  const filters = {
    ...getUserCoursesFilters({
      currentCurriculumProgramType,
      currentCurriculumProgramId: currentCurriculumProgram.id,
      organizationCurriculumPrograms,
    }),
    ...(!isCurrentAcademicYear && { academicYear }),
  };

  if (
    userEntityType == "STUDENT" &&
    ACLStore.can("FeatureFlag:StudentArchivedClasses")
  ) {
    filters["includeRemovedCoursesForStudent"] = true;
  }

  const courseGradeFilters = {
    academicYearId: academicYear,
  };

  return {
    id: userId,
    type: userEntityType,
    filters,
    courseGradeFilters,
    isCurriculumProgramFree,
  };
};

export const getSelectedSubjectForUnit = ({ unitPlan, courseSubjectIds }) => {
  const unitPlanType = _.get(unitPlan, "unitType.value", "");
  if (getIsOtherSubjectImportEnabled({ unitPlan, courseSubjectIds })) {
    if (
      _.includes(
        [UBD_SUBJECT_SPECIFIC, MYP_SUBJECT_SPECIFIC, DP_SUBJECT_SPECIFIC],
        unitPlanType
      )
    ) {
      return _.size(courseSubjectIds) > 1 ? [] : courseSubjectIds;
    } else {
      const _courseSubjectIds =
        _.size(courseSubjectIds) > 1 ? [] : courseSubjectIds;
      if (_.isEmpty(_.get(unitPlan, "subjects.value", []))) {
        return _courseSubjectIds;
      }
      return _.uniq(
        _.concat(_courseSubjectIds, _.get(unitPlan, "subjects.value", []))
      );
    }
  }

  return !_.isEqual(
    _.get(unitPlan, "curriculumProgram.type", "IB_PYP"),
    CURRICULUM_TYPE_PYP
  )
    ? _.get(unitPlan, "subjects.value", [])
    : [];
};

//check weather the feature flag is on for the current org and is the unit plan we are importing contains subject which are not present in the course
export const getIsOtherSubjectImportEnabled = ({
  unitPlan,
  courseSubjectIds,
}) => {
  const unitPlanSubjects = _.get(unitPlan, "subjects.value", []);
  const curriculumType = _.get(
    unitPlan,
    "curriculumProgram.type",
    CURRICULUM_TYPE_PYP
  );
  return (
    !_.isEqual(CURRICULUM_TYPE_PYP, curriculumType) &&
    (!_.isEmpty(_.difference(unitPlanSubjects, courseSubjectIds)) ||
      _.isEmpty(unitPlanSubjects))
  );
};

export const updateUserCourses = ({
  removedCourses = [],
  addedCourses = [],
  portalType,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    const userId = userInfo.id;

    const state = getState();

    const courseFilters = getUserCoursesFilters({
      currentCurriculumProgramType: _.get(
        state,
        "platform.currentCurriculumProgram.type",
        ""
      ),
      currentCurriculumProgramId: state.platform.currentCurriculumProgram.id,
      organizationCurriculumPrograms:
        state.platform.organizationCurriculumPrograms,
    });

    try {
      await client.mutate({
        mutation: updateUserCoursesMutation,
        variables: {
          id: userId,
          removedCourses,
          addedCourses,
          portalType,
          filters: courseFilters,
          isCurriculumProgramFree: getCurriculumProgramFreeStatus({ state }),
        },
        refetchQueries: [
          {
            query: getPlatformUserDetailsQuery,
            variables: {
              id: userId,
              type: userInfo.userEntityType,
              portalType,
            },
          },
        ],
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }

      throw error;
    }
  };
};

export const isCourseArchived = course => {
  const { isRemovedCourseForLoggedInUser = false, isArchived = false } = course;
  return isRemovedCourseForLoggedInUser || isArchived;
};

export const getPartitionedCoursesMemoized = _.memoize(
  params => getPartitionedCourses(params),
  params => JSON.stringify(params)
);

export const getPartitionedCourses = courses => {
  return _.partition(courses, course => {
    return !isCourseArchived(course);
  });
};

export const getAcademicYearsListAndGradeList = ({
  organizationId,
  curriculumProgramId,
  selectedAcademicYear,
}) => {
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);
  const academicYears = _.get(organizationDetails, "academicYears", []);

  const academicYearsList = _.map(academicYears, academicYear => {
    const { id, startDate, endDate } = academicYear;
    return {
      label: getYearDurationFormat({ startDate, endDate }),
      value: id,
    };
  });

  const defaultAcademicYear = _.get(
    selectedAcademicYear ??
      _.find(academicYears, { isCurrentAcademicYear: true }),
    "id",
    ""
  );

  const gradeList = _.map(
    _.filter(
      _.get(organizationDetails, "grades", []),
      grade => grade.curriculumProgram.id === curriculumProgramId
    ),
    item => ({
      label: item.name,
      value: item.id,
    })
  );

  return {
    academicYearsList,
    defaultAcademicYear,
    gradeList,
  };
};

export const getGlobalHeaderWiseScreenRevampStatus = () => {
  return ACLStore.can(ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS);
};

export const getUserCoursesBasicDetails = variables => {
  return async dispatch => {
    try {
      const response = await client.query({
        query: getUserCoursesBasicDetailsQuery,
        variables,
        fetchPolicy: NETWORK_ONLY,
      });
      const coursesEdges = _.get(response, "data.node.coursesV2.edges", []);
      return _.map(coursesEdges, ({ node }) => node);
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return [];
    }
  };
};

export const getCourseDetails = variables => {
  return async dispatch => {
    try {
      const result = await client.query({
        query: getCourseDetailsQuery,
        variables,
        fetchPolicy: "network-only",
      });

      return _.get(result, "data.node", {});
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const setAndCheckCourse = ({ course_id, archivedStudentsList }) => {
  return (dispatch, getState) => {
    const userInfo = getUserInfo();
    const state = getState();
    const { id, childID, user_type, isDevUser } = userInfo;
    if (!isNumber(course_id)) {
      dispatch(
        goToPlatformHomeChildRoute({
          route: "courses",
          navigateType: "replace",
        })
      );
      return;
    }

    const academicYearId = getSelectedAcademicYearId(state);
    const courseGradeFilters = {
      academicYearId,
    };

    const { students, isDemo } = getCourseDetailsFromCache({
      id: course_id,
      courseGradeFilters,
    });

    //when user type is parent we need to fetch the detils for the selected student so we are allocating student id to userid
    const isUserTypeParent = user_type == "parent";
    const userId = isUserTypeParent ? childID : id;

    // console.log("isDevUser", isDevUser);
    let hasAccess = false;

    //For Demo Class, it's true
    if (isDemo || isDevUser) {
      hasAccess = true;
    } else {
      if (user_type != "staff") {
        hasAccess =
          !!_.find(
            _.get(students, "edges", []),
            ({ node }) => node.id == userId
          ) || archivedStudentsList.includes(userId);
      } else {
        const selectedAcademicYear = _.get(
          getState(),
          "platform.academicYearSelected",
          {}
        );
        const { id: academicYear = "" } = selectedAcademicYear;

        const variables = {
          id: userId,
          filters: {
            academicYear,
            includedCourseIds: [course_id],
            archivalState: "ALL",
          },
        };

        const courses = getUserCourseAccessQueryFromCache(variables);
        const courseEdges = _.get(courses, "coursesV2.edges", []);

        const hasListAndAccessAllClassesPermission = ACLStorage.getPermissionValue(
          LIST_AND_ACCESS_ALL_CLASSES_PERMISSION
        );

        hasAccess =
          _.size(courseEdges) > 0 || hasListAndAccessAllClassesPermission;
      }
    }

    if (hasAccess) {
      dispatch(setGradeSection(course_id));
    } else {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:not_authorized_for_class",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
      dispatch(
        goToPlatformHomeChildRoute({
          route: "courses",
          navigateType: "replace",
        })
      );
    }
  };
};

const REDUCER_HANDLERS = {
  [UPDATE_COURSE_NAVIGATION_MENU]: (state, action) => {
    const { data } = action;
    _.forOwn(data, (value, key) => {
      state = update(state, { navigationMenu: { [key]: { $set: value } } });
    });
    return state;
  },
  [RESET_COURSE_NAVIGATION_MENU]: state => {
    const { navigationMenu } = state;
    _.forOwn(navigationMenu, (value, key) => {
      state = update(state, { navigationMenu: { [key]: { $set: [] } } });
    });
    return state;
  },
};

const initialState = {
  navigationMenu: {
    topMenuList: [],
    bottomMenuList: [],
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
